import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { Shield, Smartphone, Key, Copy, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const TwoFactorAuth = ({ user, token, company }) => {
  const { toast } = useToast();
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  // Local mirror of the company-level policy flag so the toggle gives
  // instant visual feedback while the PUT to /api/company/2fa-policy
  // completes. Falls back to whatever the server sent in `company`.
  const [policyRequired, setPolicyRequired] = useState(!!company?.require_2fa);
  const [policySaving, setPolicySaving] = useState(false);

  const togglePolicy = async () => {
    const next = !policyRequired;
    setPolicySaving(true);
    setPolicyRequired(next);  // optimistic
    try {
      await axios.put(
        `${API}/company/2fa-policy`,
        { require_2fa: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: next ? '2FA Now Required' : '2FA Now Optional',
        description: next
          ? 'Every user in your company will be prompted to set up 2FA on next sign-in.'
          : 'Users can choose whether to enable 2FA.',
      });
    } catch (err) {
      // Roll back optimistic update
      setPolicyRequired(!next);
      toast({
        title: 'Could not update policy',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setPolicySaving(false);
    }
  };

  const startSetup = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSetupData(response.data);
      setShowSetupDialog(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start 2FA setup',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verifySetup = async () => {
    if (verificationCode.length !== 6) {
      toast({
        title: 'Invalid Code',
        description: 'Please enter a 6-digit code',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${API}/2fa/verify-setup`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: '2FA enabled successfully! Save your backup codes.',
      });
      
      setShowSetupDialog(false);
      setVerificationCode('');

      // Update local user object so the SecuritySetup overview page can
      // flip its "Continue" button green without a full reload (which would
      // bounce the user out of the post-onboarding flow).
      try {
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        if (stored) {
          localStorage.setItem('user', JSON.stringify({ ...stored, two_factor_enabled: true }));
        }
      } catch (_e) { /* non-fatal */ }
      window.dispatchEvent(new CustomEvent('2fa-enabled'));

      // If this component is hosted inside SecuritySetup, the parent listens
      // for the event above and updates state without reloading. Everywhere
      // else (Settings page) a reload is fine and shows the new status.
      const onSecuritySetup = window.location.pathname.startsWith('/security-setup');
      if (!onSecuritySetup) {
        window.location.reload();
      }
    } catch (error) {
      toast({
        title: 'Verification Failed',
        description: error.response?.data?.detail || 'Invalid code. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const disable2FA = async () => {
    if (disableCode.length !== 6) {
      toast({
        title: 'Invalid Code',
        description: 'Please enter a 6-digit code from your authenticator',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${API}/2fa/disable`,
        { code: disableCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: '2FA has been disabled',
      });
      
      setShowDisableDialog(false);
      setDisableCode('');
      window.location.reload();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to disable 2FA',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBackupCodes = async () => {
    try {
      const response = await axios.get(`${API}/2fa/backup-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch backup codes',
        variant: 'destructive'
      });
    }
  };

  const copyBackupCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({
      title: 'Copied',
      description: 'Backup code copied to clipboard',
    });
  };

  const copyAllBackupCodes = () => {
    navigator.clipboard.writeText(setupData?.backup_codes.join('\n'));
    toast({
      title: 'Copied',
      description: 'All backup codes copied to clipboard',
    });
  };

  const isRequired = policyRequired;
  const isEnabled = user?.two_factor_enabled;
  const isAdmin = user?.role === 'admin';

  return (
    <Card className="border-2">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50 border-b">
        <CardTitle className="flex items-center space-x-2">
          <Shield className="w-6 h-6 text-purple-600" />
          <span>Two-Factor Authentication</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {isAdmin && (
          <div
            className="flex items-start justify-between gap-3 p-4 rounded-lg"
            style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            data-testid="company-2fa-policy"
          >
            <div className="flex-1">
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                <Shield className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                Company policy: Require 2FA for all users
              </div>
              <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                {policyRequired
                  ? 'Every user must enable 2FA before they can sign in. Users without 2FA enabled are redirected to setup on next sign-in.'
                  : '2FA is optional. Individual users can choose to enable it from this page.'}
              </p>
            </div>
            <button
              role="switch"
              aria-checked={policyRequired}
              onClick={togglePolicy}
              disabled={policySaving}
              className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors mt-1 disabled:opacity-60"
              style={{ background: policyRequired ? '#8B5CF6' : '#2D3748' }}
              data-testid="company-2fa-policy-toggle"
              title={policyRequired ? 'Click to make 2FA optional' : 'Click to require 2FA for everyone'}
            >
              <span
                className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform transition-transform"
                style={{ transform: policyRequired ? 'translateX(22px)' : 'translateX(2px)', marginTop: 2 }}
              />
            </button>
          </div>
        )}

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h4 className="font-semibold text-white">Security Status</h4>
              {isEnabled ? (
                <Badge className="bg-green-100 text-green-800 border-green-300">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Enabled
                </Badge>
              ) : (
                <Badge className="bg-[#0F1923] text-white border-[#2D3748]">
                  Disabled
                </Badge>
              )}
              {isRequired && (
                <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Required by Admin
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-400">
              {isEnabled 
                ? 'Your account is protected with Microsoft Authenticator' 
                : 'Add an extra layer of security to your account'}
            </p>
            {isRequired && !isEnabled && (
              <p className="text-sm text-orange-600 font-semibold mt-2">
                ⚠️ Your administrator requires 2FA. Please enable it to continue using the app.
              </p>
            )}
          </div>
        </div>

        <div className="flex space-x-3">
          {!isEnabled ? (
            <Button
              onClick={startSetup}
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Smartphone className="w-4 h-4 mr-2" />
              )}
              Enable 2FA
            </Button>
          ) : (
            <>
              <Button
                onClick={fetchBackupCodes}
                variant="outline"
              >
                <Key className="w-4 h-4 mr-2" />
                View Backup Codes
              </Button>
              {!isRequired && (
                <Button
                  onClick={() => setShowDisableDialog(true)}
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  Disable 2FA
                </Button>
              )}
            </>
          )}
        </div>

        {/* Setup Dialog */}
        <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Shield className="w-6 h-6 text-purple-600" />
                <span>Set Up Two-Factor Authentication</span>
              </DialogTitle>
              <DialogDescription>
                Use Microsoft Authenticator or any TOTP app
              </DialogDescription>
            </DialogHeader>

            {setupData && (
              <div className="space-y-6">
                {/* Step 1 */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm mr-2">1</span>
                    Scan QR Code
                  </h4>
                  <div className="bg-[#1A2433] p-4 rounded-lg border-2 border-[#2D3748] flex justify-center">
                    <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    Open Microsoft Authenticator and scan this code
                  </p>
                </div>

                {/* Step 2 */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm mr-2">2</span>
                    Enter Verification Code
                  </h4>
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl font-mono tracking-wider h-14"
                  />
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                {/* Backup Codes */}
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 mb-2 flex items-center">
                    <Key className="w-4 h-4 mr-2" />
                    Save Your Backup Codes
                  </h4>
                  <p className="text-xs text-yellow-800 mb-3">
                    Keep these codes safe! You'll need them if you lose access to your authenticator.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {setupData.backup_codes.map((code, idx) => (
                      <div key={idx} className="bg-[#1A2433] p-2 rounded border border-yellow-300 text-center font-mono text-sm">
                        {code}
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={copyAllBackupCodes}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy All Codes
                  </Button>
                </div>

                {/* Verify Button */}
                <Button
                  onClick={verifySetup}
                  disabled={isLoading || verificationCode.length !== 6}
                  className="w-full bg-purple-600 hover:bg-purple-700 h-12"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  )}
                  Verify and Enable 2FA
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Disable Dialog */}
        <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
              <DialogDescription>
                Enter a code from your authenticator app to disable 2FA
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Disabling 2FA will reduce your account security.
                </p>
              </div>

              <div>
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl font-mono tracking-wider h-14"
                />
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={disable2FA}
                  disabled={isLoading || disableCode.length !== 6}
                  variant="destructive"
                  className="flex-1"
                >
                  Disable 2FA
                </Button>
                <Button
                  onClick={() => setShowDisableDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Backup Codes Dialog */}
        <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Key className="w-6 h-6 text-purple-600" />
                <span>Your Backup Codes</span>
              </DialogTitle>
              <DialogDescription>
                Use these codes if you lose access to your authenticator app
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 mb-3">
                  <strong>Important:</strong> Each backup code can only be used once. 
                  Keep them in a safe place.
                </p>
                {backupCodes.length > 0 ? (
                  <div className="space-y-2">
                    {backupCodes.map((code, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-[#1A2433] p-3 rounded border border-yellow-300"
                      >
                        <span className="font-mono font-semibold">{code}</span>
                        <Button
                          onClick={() => copyBackupCode(code)}
                          variant="ghost"
                          size="sm"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-400">No backup codes remaining</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TwoFactorAuth;
